"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowLeft, Send, Mail, Shield, FileText, AlertTriangle } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "general", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const u = (field: string, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production, this would POST to an API that sends an email or creates a support ticket
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-[#050505] text-neutral-300 flex items-center justify-center">
        <div className="max-w-md mx-4 text-center">
          <div className="h-16 w-16 bg-green-900/20 border border-green-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <Send className="w-6 h-6 text-green-500" />
          </div>
          <h1 className="text-xl font-black text-white uppercase tracking-tight mb-3">Message Sent</h1>
          <p className="text-sm text-neutral-400 mb-6">We have received your message and will respond within 24-48 hours. Check your email for a reply.</p>
          <Link href="/" className="text-red-500 font-bold uppercase text-sm hover:text-red-400 transition">Back to Home</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050505] text-neutral-300">
      <div className="max-w-2xl mx-auto px-4 py-12">

        <Link href="/support" className="flex items-center gap-2 text-neutral-500 hover:text-white text-sm mb-6 transition">
          <ArrowLeft className="w-4 h-4" /> Back to Help Center
        </Link>

        <h1 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Contact Us</h1>
        <p className="text-sm text-neutral-500 mb-10">Send us a message and we will get back to you within 24-48 hours.</p>

        {/* Quick links */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
          {[
            { icon: Mail, label: "General Support", email: "support@cellbloxkbarbie.com" },
            { icon: Shield, label: "DMCA / Copyright", email: "dmca@cellbloxkbarbie.com" },
            { icon: AlertTriangle, label: "Report Abuse", email: "safety@cellbloxkbarbie.com" },
          ].map((item) => (
            <div key={item.label} className="p-4 bg-[#0a0a0a] border border-neutral-800/60 rounded-lg">
              <item.icon className="w-4 h-4 text-red-500 mb-2" />
              <div className="text-xs font-bold text-white uppercase tracking-wider mb-1">{item.label}</div>
              <div className="text-[11px] text-neutral-500 break-all">{item.email}</div>
            </div>
          ))}
        </div>

        {/* Contact form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1.5">Your Name</label>
              <input type="text" value={form.name} onChange={(e) => u("name", e.target.value)} required className="w-full bg-[#0a0a0a] border border-neutral-800 rounded-lg px-4 py-3 text-sm text-white placeholder-neutral-600 focus:border-red-600 focus:outline-none transition" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1.5">Email Address</label>
              <input type="email" value={form.email} onChange={(e) => u("email", e.target.value)} required className="w-full bg-[#0a0a0a] border border-neutral-800 rounded-lg px-4 py-3 text-sm text-white placeholder-neutral-600 focus:border-red-600 focus:outline-none transition" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1.5">Subject</label>
            <select value={form.subject} onChange={(e) => u("subject", e.target.value)} className="w-full bg-[#0a0a0a] border border-neutral-800 rounded-lg px-4 py-3 text-sm text-white focus:border-red-600 focus:outline-none transition">
              <option value="general">General Inquiry</option>
              <option value="account">Account Issue</option>
              <option value="billing">Billing / Credits</option>
              <option value="creator">Creator Support</option>
              <option value="technical">Technical Problem</option>
              <option value="dmca">DMCA / Copyright</option>
              <option value="safety">Safety / Abuse Report</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1.5">Message</label>
            <textarea value={form.message} onChange={(e) => u("message", e.target.value)} required rows={6} placeholder="Describe your issue or question in detail..." className="w-full bg-[#0a0a0a] border border-neutral-800 rounded-lg px-4 py-3 text-sm text-white placeholder-neutral-600 focus:border-red-600 focus:outline-none transition resize-none" />
          </div>

          <button type="submit" className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-lg font-bold uppercase text-sm tracking-wider transition">
            <Send className="w-4 h-4" /> Send Message
          </button>
        </form>
      </div>
    </main>
  );
}
